import React from 'react'
import { Typography, TextField, Box, Stack, Select, FormControl, InputLabel, MenuItem } from '@mui/material'
import { addMinutes, addSeconds, endOfYear, format, startOfYear } from 'date-fns'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker'
import axios from 'axios'
import moment from 'moment-timezone'
import LoadingButton from '@mui/lab/LoadingButton'
import { useAuth } from '../../../context/useAuth'

export default function Generator() {
  const { csrf } = useAuth()
  const [params, setParams] = React.useState<any>({
    start: startOfYear(new Date()),
    end: addMinutes(addSeconds(endOfYear(new Date()), 1), -15),
    frequency: 900,
    latitude: 51.064583,
    longitude: 3.737385,
    timezone: 'Europe/Brussels',
    utc_offset: 7200
  })
  const [eldemand, setEldemand] = React.useState<any>({
    nr_of_households: undefined,
    yearly_consumption: undefined,
    load_profile: 'VREG',
  })

  const [pvinstallation, setPVinstallation] = React.useState<any>({
    P_STC: 352.0,
    selfheating_capacity: 0.056,
    nameplate_derating: 0.05,
    module_mismatch: 0.015,
    cable_loss: 0.015,
    soiling: 0.01,
    tilt_angle: 35.0,
    azimuth: 0.0,
    n_panels: undefined,
    P_inv: 100000.0,
    inv_nameplate_derating: 0.015,
  })

  const [error, setError] = React.useState<any>(undefined)
  const [error2, setError2] = React.useState<any>(undefined)
  const [error3, setError3] = React.useState<any>(undefined)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [loading2, setLoading2] = React.useState<boolean>(false)
  const [loading3, setLoading3] = React.useState<boolean>(false)

  const checkParams = () => {
    if (params.start && params.end && params.frequency && params.end > params.start) return true
    else return false
  }

  const checkEldemand = () => {
    if (eldemand.nr_of_households && eldemand.yearly_consumption && eldemand.load_profile) return true
    else return false
  }

  const checkPVinstallation = () => {
    if (pvinstallation.P_STC && pvinstallation.selfheating_capacity && pvinstallation.nameplate_derating && 
      pvinstallation.module_mismatch && pvinstallation.cable_loss && pvinstallation.soiling &&
      pvinstallation.tilt_angle && pvinstallation.azimuth && pvinstallation.n_panels &&
      pvinstallation.P_inv && pvinstallation.inv_nameplate_derating) return true
    else return false
  }

  const download = (res: any) => {
    const url = window.URL.createObjectURL(new Blob([res.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'input_data.json')
    document.body.appendChild(link)
    link.click()
  }

  const timezones = moment.tz.names();

  return (
    <Box sx={{ p: 2, pt: 1.5 }}>
      <Typography variant='h6'>Time Parameters</Typography>
      <Typography variant='body1'>Generate input data from 'start' to 'end' with a defined 'frequency'.</Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack sx={{ width: 500, mt: 2 }} spacing={2}>
          <MobileDateTimePicker
            minutesStep={15}
            value={params.start}
            ampm={false}
            label={'start'}
            onChange={(value) =>
              setParams((curr: any) => {
                return { ...curr, start: value }
              })
            }
          />
          <MobileDateTimePicker
            minutesStep={15}
            value={params.end}
            ampm={false}
            label={'end'}
            onChange={(value) =>
              setParams((curr: any) => {
                return { ...curr, end: value }
              })
            }
          />
          <TextField
            defaultValue={params.frequency}
            type='number'
            size='small'
            label='frequency [s]'
            onChange={(e) =>
              setParams((curr: any) => {
                return { ...curr, frequency: e.target.value }
              })
            }
          />
        </Stack>
      </LocalizationProvider>

      <Typography variant='h6'>Location Parameters</Typography>
      <Typography variant='body1'>Generate input data for a certain location (especially relevant for RES production).</Typography>
        <Stack sx={{ width: 500, mt: 2 }} spacing={2}>
          <TextField
            defaultValue={params.latitude}
            type='number'
            size='small'
            label='Latitude'
            onChange={(e) =>
              setParams((curr: any) => {
                return { ...curr, latitude: e.target.value }
              })
            }
          />
          <TextField
            defaultValue={params.longitude}
            type='number'
            size='small'
            label='Longitude'
            onChange={(e) =>
              setParams((curr: any) => {
                return { ...curr, longitude: e.target.value }
              })
            }
          />
          <FormControl sx={{ m: 1, minWidth: 120 }} size='small'>
          <InputLabel>timezone</InputLabel>
          <Select
            value={params.timezone}
            label='timezone'
            onChange={(e) =>
              setParams((curr: any) => {
                return { ...curr, timezone: e.target.value }
              })
            }
          >
            {timezones.map((tz: string) => (
              <MenuItem key={tz} value={tz}>
                {tz}
              </MenuItem>
            ))}
          </Select>
          </FormControl>
          <TextField
            defaultValue={params.utc_offset}
            type='number'
            size='small'
            label='UTC offset [s]'
            onChange={(e) =>
              setParams((curr: any) => {
                return { ...curr, utc_offset: e.target.value }
              })
            }
          />
        </Stack>
      <Typography variant='h6' sx={{ mt: 2 }}>
        Electricity Demand Data
      </Typography>
      <Typography variant='body1'>
        Generate electricity demand input data based on a number of households and their yearly consumption (kWh). Real
        Load Profile 0 Normalized (RLP0N) is used for load calculations.
      </Typography>
      <Stack sx={{ width: 500, mt: 2 }} spacing={2}>
        <TextField
          type='number'
          size='small'
          label='nr_of_households'
          onChange={(e) =>
            setEldemand((curr: any) => {
              return { ...curr, nr_of_households: e.target.value }
            })
          }
        />
        <TextField
          type='number'
          size='small'
          label='yearly_consumption [kWh]'
          onChange={(e) =>
            setEldemand((curr: any) => {
              return { ...curr, yearly_consumption: e.target.value }
            })
          }
        />
        <FormControl sx={{ m: 1, minWidth: 120 }} size='small'>
          <InputLabel>load_profile</InputLabel>
          <Select
            value={eldemand.load_profile}
            label='load_profile'
            onChange={(e) =>
              setEldemand((curr: any) => {
                return { ...curr, load_profile: e.target.value }
              })
            }
          >
            <MenuItem value={'VREG'}>VREG</MenuItem>
            <MenuItem value={'british_load_profiles'}>british_load_profiles</MenuItem>
          </Select>
        </FormControl>

        <LoadingButton
          loading={loading}
          onClick={async () => {
            setLoading(true)
            setError(undefined)
            let fd = new FormData()
            fd.append('start', format(new Date(params.start), 'yyyy-MM-dd HH:mm'))
            fd.append('end', format(new Date(params.end), 'yyyy-MM-dd HH:mm'))
            fd.append('frequency', params.frequency)
            fd.append('nr_of_households', eldemand.nr_of_households)
            fd.append('yearly_consumption', eldemand.yearly_consumption)
            fd.append('load_profile', eldemand.load_profile)
            try {
              const res = await axios.post(`${process.env.REACT_APP_API}/generate/electricity_demand`, fd, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  'X-CSRFToken': csrf,
                },
                responseType: 'blob',
                withCredentials: true,
              })

              const blob = new Blob([res.data], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'electricity_demand_input_file.csv');
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              setLoading(false)
            } catch (err: any) {
              setLoading(false)
              const error = err
              if (error.response.data._error) setError(error.response.data._error)
            }
          }}
          disabled={!(checkEldemand() && checkParams())}
          variant='contained'
          disableElevation
        >
          Generate Data
        </LoadingButton>
      </Stack>
      <Typography variant='caption' color='error' sx={{ mx: 2, mt: -1 }}>
        {error}
      </Typography>

      <Typography variant='h6' sx={{ mt: 2 }}>
        Weather Data
      </Typography>
      <Typography variant='body1'>
      Generate weather input data based on a default Typical Meteorological Year (TMY) file.
      </Typography>
      <Stack sx={{ width: 500, mt: 1 }} spacing={2}>
        <LoadingButton
          loading={loading2}
          onClick={async () => {
            setError2(undefined)
            setLoading2(true)
            let fd = new FormData()
            fd.append('start', format(new Date(params.start), 'yyyy-MM-dd HH:mm'))
            fd.append('end', format(new Date(params.end), 'yyyy-MM-dd HH:mm'))
            fd.append('frequency', params.frequency)
            try {
              const res = await axios.post(`${process.env.REACT_APP_API}/generate/weather`, fd, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  'X-CSRFToken': csrf,
                },
                responseType: 'blob',
                withCredentials: true,
              })

              const blob = new Blob([res.data], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'weather_input_file.csv');
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              setLoading2(false)
            } catch (err: any) {
              setLoading2(false)
              const error = await err.response.data.text()
              if (error) setError2(JSON.parse(error)._error)
            }
          }}
          disabled={!checkParams()}
          variant='contained'
          disableElevation
          sx={{ minWidth: 190 }}
        >
          Generate Data
        </LoadingButton>
      </Stack>
      <Stack sx={{ mt: 1 }}>
        <Typography variant='caption' color='error'>
          {error2}
        </Typography>
      </Stack>
      
      <Typography variant='h6' sx={{ mt: 2 }}>
        Solar energy production Data
      </Typography>
      <Typography variant='body1'>
        Generate solar energy production data based on irradiation and temperature from a default Typical Meteorological Year (TMY) and a PV model.
      </Typography>
      <Stack sx={{ width: 500, mt: 1 }} spacing={2}>
      <TextField
          defaultValue={pvinstallation.n_panels}
          type='number'
          size='small'
          label='Number of panels'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, n_panels: e.target.value }
            })
          }
        /><TextField
          defaultValue={pvinstallation.tilt_angle}
          type='number'
          size='small'
          label='Panel tilt angle [°]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, tilt_angle: e.target.value }
            })
          }
        />
        <TextField
          defaultValue={pvinstallation.azimuth}
          type='number'
          size='small'
          label='Azimuth (east of south: < 0; west of south: > 0) [°]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, azimuth: e.target.value }
            })
          }
        />
        <TextField
          defaultValue={pvinstallation.P_STC}
          type='number'
          size='small'
          label='Panel output power under standard test conditions [W]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, P_STC: e.target.value }
            })
          }
        />
        <TextField
          defaultValue={pvinstallation.selfheating_capacity}
          type='number' 
          size='small'
          label='Self-heating capacity [°C]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, selfheating_capacity: e.target.value }
            })
          }
        />
        <TextField
          defaultValue={pvinstallation.nameplate_derating}
          type='number'
          size='small'
          label='Nameplate derating losses [-]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, nameplate_derating: e.target.value }
            })
          }
        />
        <TextField
          defaultValue={pvinstallation.module_mismatch}
          type='number'
          size='small'
          label='Module mismatch losses [-]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, module_mismatch: e.target.value }
            })
          }
        />
        <TextField
          defaultValue={pvinstallation.cable_loss}
          type='number'
          size='small'
          label='Cable losses [-]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, cable_loss: e.target.value }
            })
          }
        />
        <TextField
          defaultValue={pvinstallation.soiling}
          type='number'
          size='small'
          label='Soiling losses [-]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, soiling: e.target.value }
            })
          }
        />
        <TextField
          defaultValue={pvinstallation.P_inv}
          type='number'
          size='small'
          label='Rated power of the inverter [W]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, P_inv: e.target.value }
            })
          }
        />
        <TextField
          defaultValue={pvinstallation.inv_nameplate_derating}
          type='number'
          size='small'
          label='Nameplate derating losses of the inverter [-]'
          onChange={(e) =>
            setPVinstallation((curr: any) => {
              return { ...curr, inv_nameplate_derating: e.target.value }
            })
          }
        />
        <LoadingButton
          loading={loading3}
          onClick={async () => {
            setError3(undefined)
            setLoading3(true)
            let fd = new FormData()
            fd.append('start', format(new Date(params.start), 'yyyy-MM-dd HH:mm'))
            fd.append('end', format(new Date(params.end), 'yyyy-MM-dd HH:mm'))
            fd.append('frequency', params.frequency)
            fd.append('latitude', params.latitude)
            fd.append('longitude', params.longitude)
            fd.append('timezone', params.timezone)
            fd.append('utc_offset', params.utc_offset)
            fd.append('P_STC', pvinstallation.P_STC)
            fd.append('selfheating_capacity', pvinstallation.selfheating_capacity)
            fd.append('nameplate_derating', pvinstallation.nameplate_derating)
            fd.append('module_mismatch', pvinstallation.module_mismatch)
            fd.append('cable_loss', pvinstallation.cable_loss)
            fd.append('soiling', pvinstallation.soiling)
            fd.append('tilt_angle', pvinstallation.tilt_angle)
            fd.append('azimuth', pvinstallation.azimuth)
            fd.append('n_panels', pvinstallation.n_panels)
            fd.append('P_inv', pvinstallation.P_inv)
            fd.append('inv_nameplate_derating', pvinstallation.inv_nameplate_derating)
            try {
              const res = await axios.post(`${process.env.REACT_APP_API}/generate/pv_production`, fd, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  'X-CSRFToken': csrf,
                },
                responseType: 'blob',
                withCredentials: true,
              })

              const blob = new Blob([res.data], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'pv_production_input_file.csv');
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              setLoading3(false)
            } catch (err: any) {
              setLoading3(false)
              const error = await err.response.data.text()
              if (error) setError3(JSON.parse(error)._error)
            }
          }}
          disabled={!(checkPVinstallation() && checkParams())}
          variant='contained'
          disableElevation
          sx={{ minWidth: 190 }}
        >
          Generate Data
        </LoadingButton>
      </Stack>
      <Stack sx={{ mt: 1 }}>
        <Typography variant='caption' color='error'>
          {error3}
        </Typography>
      </Stack>
    </Box>
  )
}
